import { FC, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import { colors } from '../../theme/tokens';

// The XD list/grid switch, shared by the Home and Résultats since the
// "Flux 2" revision put it on both (DESIGN.md, "Revisión desktop"). One
// stored preference: switching view on either screen carries to the other.

export type DocumentsView = 'list' | 'grid';

// Client feedback: the list/grid switch must not be offered yet. While this
// is off the switch is not rendered and the view is always the list, even if
// a grid preference was stored earlier. Flip it back on to restore the grid.
export const DOCUMENTS_VIEW_TOGGLE_ENABLED = false;

export const VIEW_STORAGE_KEY = 'ARCHIVOS_DOCUMENTS_VIEW';

const readView = (): DocumentsView =>
  DOCUMENTS_VIEW_TOGGLE_ENABLED &&
  localStorage.getItem(VIEW_STORAGE_KEY) === 'grid'
    ? 'grid'
    : 'list';

export const useDocumentsView = (): [
  DocumentsView,
  (next: DocumentsView | null) => void,
] => {
  const [view, setView] = useState<DocumentsView>(readView);

  // `null` is MUI notifying a click on the already-active button.
  const change = (next: DocumentsView | null) => {
    if (!next) return;
    setView(next);
    localStorage.setItem(VIEW_STORAGE_KEY, next);
  };

  return [view, change];
};

const ViewToggleButtons: FC<{
  view: DocumentsView;
  onChange: (next: DocumentsView | null) => void;
}> = ({ view, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={view}
    onChange={(_, next: DocumentsView | null) => onChange(next)}
    aria-label="Affichage des documents"
    sx={{
      '& .MuiToggleButton-root': {
        color: colors.muted,
        borderColor: colors.border,
        px: 1.25,
      },
      '& .MuiToggleButton-root.Mui-selected': {
        color: colors.white,
        backgroundColor: colors.primary,
        '&:hover': { backgroundColor: colors.primary },
      },
    }}
  >
    <ToggleButton value="list" aria-label="Vue liste">
      <ViewListOutlinedIcon fontSize="small" />
    </ToggleButton>
    <ToggleButton value="grid" aria-label="Vue grille">
      <GridViewOutlinedIcon fontSize="small" />
    </ToggleButton>
  </ToggleButtonGroup>
);

const DocumentsViewToggle: FC<{
  view: DocumentsView;
  onChange: (next: DocumentsView | null) => void;
}> = (props) =>
  DOCUMENTS_VIEW_TOGGLE_ENABLED ? <ViewToggleButtons {...props} /> : null;

export default DocumentsViewToggle;
